import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { request } from "../../../utils/api";
import { toast } from "react-toastify";
import Loading from "../utilComponent/Loading";
import Pagination from "../utilComponent/Pagination";
import { formatYYYYMMDD } from "../../../utils";

const ProductLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const getProductList = async () => {
    try {
      setLoading(true);
      const response = await request({
        url: `products`,
        method: "GET",
      });
      if (response.status && response.data) {
        setProducts(response.data.items || []);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getLogsByProduct = async (page = 1, productId = selectedProduct) => {
    if (!productId) return;

    try {
      setLoading(true);
      const res = await request({
        url: `product-activity-logs?product_id=${productId}&page=${page}&per_page=${pagination.per_page}`,
        method: "GET",
      });

      if (res?.status && res?.data) {
        setLogs(
          res.data.items.map((item) => ({
            id: item.id,
            activity_description: item.activity_description,
            created_at:formatYYYYMMDD(item.created_at),
            product_name: item.product?.name || "N/A",
            product_sku: item.product?.sku || "N/A",
            user_name: item.user?.name || "N/A",
            user_role: item.user?.role || "N/A",
            updated_at: formatYYYYMMDD(item.updated_at),
            action: item.activity_type,
            old_values: item.old_values || null,
            new_values: item.new_values || null,
            metadata: item.metadata || {},
          }))
        );

        setPagination(res.data.pagination);
      }
    } catch (err) {
      toast.error("Failed to fetch product logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductList();
  }, []);

  return (
    <div>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Products</li>
            <li className="breadcrumb-item active">Product Logs</li>
          </ol>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
        <div className="container-fluid">
          <div className="card shadow-sm">
            <div className="card-header">
              <h6 className="card-title m-0">Product Logs</h6>
            </div>

            <div className="card-body">
              <div className="row mb-3">
                {/* Product Dropdown */}
                <div className="col-md-6">
                  <select
                    className="form-select"
                    value={selectedProduct}
                    onChange={(e) => {
                      setSelectedProduct(e.target.value);
                      getLogsByProduct(1, e.target.value);
                    }}
                  >
                    <option value="">Select a product</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {loading ? (
                <Loading />
              ) : (
                <div className="row">
                  {logs.length ? (
                    logs.map((item) => (
                      <div key={item.id} className="col-xl-4 col-lg-6 mb-4">
                        <div className="card h-100 shadow-sm">
                          <div className="card-body">
                            <h6 className="fw-bold mb-2">{item.product_name}</h6>
                            <p className="mb-1">
                              🆔 <strong>SKU:</strong> {item.product_sku || "N/A"}
                            </p>

                            <p className="mb-1">
                              🏷️ <strong>Label:</strong> {item.metadata?.label_name || "N/A"}
                            </p>

                            <p className="mb-1">
                              👤 <strong>User:</strong> {item.user_name} (
                              <span className="text-muted">{item.user_role}</span>)
                            </p>

                            <p className="mb-1">
                              🔄 <strong>Action Type:</strong>{" "}
                              <span className="badge bg-info text-dark">{item.action}</span>
                            </p>

                            <p className="mb-1">
                              📜 <strong>Action Description:</strong> <br />
                              {item.activity_description}
                            </p>

                            {item.old_values && (
                              <p className="mb-1">
                                🕰️ <strong>Old Values:</strong>{" "}
                                <pre className="small">{JSON.stringify(item.old_values, null, 2)}</pre>
                              </p>
                            )}

                            {item.new_values && (
                              <p className="mb-1">
                                ✅ <strong>New Values:</strong>{" "}
                                <pre className="small">{JSON.stringify(item.new_values, null, 2)}</pre>
                              </p>
                            )}

                            {item.metadata && (
                              <p className="mb-1">
                                🗂️ <strong>Metadata:</strong>{" "}
                                <pre className="small">{JSON.stringify(item.metadata, null, 2)}</pre>
                              </p>
                            )}

                            <p className="mb-0">
                              📅 <strong>Updated:</strong> {item.updated_at}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-5">No product logs found</div>
                  )}

                </div>
              )}

              {selectedProduct && pagination.last_page > 1 && (
                <Pagination
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={(page) => getLogsByProduct(page, selectedProduct)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductLogs;
